import { Component, OnInit, OnDestroy, ApplicationRef } from '@angular/core';
import { ProjectService } from '../../services/project.service';
import { Router, Event, NavigationEnd, ActivatedRoute } from '@angular/router';
import { ModalService, MODAL } from '../../services/modal.service';
import { UserService } from '../../services/user.service';
import { Subscription } from 'rxjs';
import { CurrentService } from '../../services/current.service';
import { BymeService } from '../../services/byme.service';
import { GTM } from 'src/app/services/gtm.service';
import { DatasService } from 'src/app/services/datas.service';
import { ProjectDuplicate } from 'src/app/model/project-duplicate';
import { ProjectData } from 'src/app/model/projectData';

@Component({
  selector: 'ng-hbm-bdi-project',
  templateUrl: './project.component.html'
})
export class ProjectComponent implements OnInit, OnDestroy {

  projectData: any;
  private _routeChangeSubscription: Subscription;
  noCache: string;
  mainImageUrl: string;
  vendorContext: boolean;
  customerName: string;
  originalProjectId: number;

  constructor(
    public project: ProjectService,
    private route: ActivatedRoute,
    private router: Router,
    public modal: ModalService,
    public customer: UserService,
    public appRef: ApplicationRef,
    public byMe: BymeService,
    public current: CurrentService,
    private datas: DatasService,
    private gtm: GTM
  ) {
    this.vendorContext = false;

    if (this.customer.userDatas) {
      this.customerName = this.customer.userDatas.completeName;
      this.vendorContext = this.customer.userDatas.vendor;
      if (this.customer.userDatas.otherAccount) {
        this.customerName = this.customer.userDatas.otherAccount.completeName ? this.customer.userDatas.otherAccount.completeName : "";
      }
    }

    this._routeChangeSubscription = this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.loadProject(this.route.snapshot.params['projectUUID']);
      }
    });
  }

  async loadProject(projectUUID: string): Promise<void> {
    this.modal.isLoading = true;
    this.projectData = await this.project.getDetails(projectUUID, this.customer.applicationId);
    this.noCache = this.projectData.dateUpdate.replace(/[\W]+/g, "");

    if (this.projectData.medias.renderHQ.length > 0) {
      // always select latest realistic images as overview image
      this.mainImageUrl = this.projectData.medias.renderHQ[this.projectData.medias.renderHQ.length - 1].url;
    }
    else if (this.projectData.medias.thumbnail.length > 1) {
      // always use the second thumbnail images as overview image, as it has better resolution
      this.mainImageUrl = this.projectData.medias.thumbnail[1].url;
    }
    else {
      // if there is not realistic image or thumbnail, we use a place holder image as overview image
      this.mainImageUrl = "assets/img/placeholders/bdi-project@2x.png";
    }

    this.modal.isLoading = false;
    this.appRef.tick();

    let projectDuplicate: ProjectDuplicate = await this.datas.getProjectDuplicate(this.projectData.projectID);
    if (projectDuplicate?.originalProjectId) {
      this.originalProjectId = projectDuplicate.originalProjectId;
    } else {
      this.originalProjectId = this.projectData.projectID
    }

    this.gtm.trackPage('project', null, null, {current_project_id: this.projectData.projectID, original_project_id: this.originalProjectId});      
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this._routeChangeSubscription.unsubscribe();
  }

  zoomImage(src: string): void {
    this.gtm.trackEvent('click', 'project', 'zoom-image', {current_project_id: this.projectData.projectID, original_project_id: this.originalProjectId});
    this.modal.display(MODAL.IMAGE_ZOOM, { imgUrl: src });
  }

  backToProjects() {
    this.gtm.trackEvent('click', 'project', 'back-to-projects');
    this.modal.display(MODAL.PROJECTS, { customerId: this.customer.userDatas.hbmId });
  }

  updateProperty(value: any, key: string) {
    let _cleanValue: string = value.split('<br>').filter(l => (l != '')).map(l => l.replace(/(\r\n|\n\r|\r|\n)/g, "")).join('\n');

    switch (key) {
      case "title":
        this.gtm.trackEvent('click', 'project', 'edit-title', {current_project_id: this.projectData.projectID, original_project_id: this.originalProjectId});
        this.project.updateNameAndDescription(this.projectData.projectUUID, _cleanValue, this.projectData.description).then(() => {
          this.projectData.projectName = _cleanValue;
          this.appRef.tick();
        });
        break;

      case "description":
        this.gtm.trackEvent('click', 'project', 'edit-description', {current_project_id: this.projectData.projectID, original_project_id: this.originalProjectId});
        this.project.updateNameAndDescription(this.projectData.projectUUID, this.projectData.projectName, _cleanValue).then(() => {
          this.projectData.description = _cleanValue;
          this.appRef.tick();
        });
        break;

      case "notes":
        this.gtm.trackEvent('click', 'project', 'edit-notes', {current_project_id: this.projectData.projectID, original_project_id: this.originalProjectId});
        this.project.updateManualNotes(this.projectData.projectUUID, this.customer.applicationId, _cleanValue).then(() => {
          this.projectData.manualNotes = _cleanValue;
          this.appRef.tick();
        });
        break;

      default:
        break;
    }
  }

  editProject(project: any, event: any) {
    event.stopPropagation();
    this.current.project = {
      id: project.projectID,
      uuid: project.projectUUID,
      name: project.projectName,
      description: project.description,
      locked: project.is_locked,
      originalId: this.originalProjectId
    }
    this.gtm.trackEvent('click', 'project', 'edit-project');
    this.byMe.prepareBymeSettingsForLoadingProject();
    this.router.navigate([this.customer.context + '/configuration']);
    this.modal.close();
  }

  duplicateProject(projectUUID: string, projectID: number, projectName: string, event: any) {
    this.gtm.trackEvent('click', 'project', 'duplicate-project', {current_project_id: this.projectData.projectID, original_project_id: this.originalProjectId});
    event.stopPropagation();
    this.modal.display(MODAL.PROJECT_DUPLICATE, {
      projectID: projectID,
      projectUUID: projectUUID,
      projectName: projectName,
      onClose: (clonedProjectUUID) => {
        if (clonedProjectUUID) {
          this.router.navigate(['projects', clonedProjectUUID]);
          this.current.initSession();
          this.modal.close();
        } else {
          this.modal.close();
        }
      }
    });
  }

  deleteProject(projectID: number, event: any) {
    this.gtm.trackEvent('click', 'project', 'delete-project', {current_project_id: this.projectData.projectID, original_project_id: this.originalProjectId});
    event.stopPropagation();
    this.modal.display(MODAL.PROJECT_DELETE, {
      projectID: projectID,
      onClose: (projectDeleted) => {
        if (projectDeleted) {
          this.router.navigate(['']);
          this.modal.display(MODAL.PROJECTS, { customerId: this.customer.userDatas.hbmId });
        } else {
          this.modal.close();
        }
      }
    });
  }

  lockProject(projectID: number, event: any) {
    this.gtm.trackEvent('click', 'project', 'lock-project', {current_project_id: this.projectData.projectID, original_project_id: this.originalProjectId});
    event.stopPropagation();
    this.project.lock(projectID).then(() => {
      this.projectData.is_locked = true;
    }, (error) => {
      console.error(error);
    })
  }

  unlockProject(projectID: number, event: any) {
    this.gtm.trackEvent('click', 'project', 'unlock-project', {current_project_id: this.projectData.projectID, original_project_id: this.originalProjectId});
    event.stopPropagation();
    this.project.unlock(projectID).then(() => {
      this.projectData.is_locked = false;
    }, (error) => {
      console.error(error);
    })
  }
}
